import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, resolveMediaUrl } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { SiteHierarchyCheckboxList } from '@/components/SiteHierarchyCheckboxList';
import { inputClass, labelClass, listRowClass } from '@/components/ui/inputStyles';

const PER_PAGE = 12;

type SiteItem = { id: number; site_name: string; parent_site_id?: number | null };

export type MiniproAdRow = {
  id: number;
  miniprogram_id: number;
  title: string;
  banner_image_path: string;
  link_type: 'entry' | 'image';
  entry_id: number;
  target_image_path: string;
  site_ids: number[];
  valid_start_date: string;
  valid_end_date: string;
  sort_order: number;
  enabled: boolean;
  is_active?: boolean;
};

type Props = {
  miniprogramId: number;
  sites: SiteItem[];
  onUpload: (file: File) => Promise<string>;
};

const emptyForm = (): Omit<MiniproAdRow, 'is_active'> => ({
  id: 0,
  miniprogram_id: 0,
  title: '',
  banner_image_path: '',
  link_type: 'entry',
  entry_id: 0,
  target_image_path: '',
  site_ids: [],
  valid_start_date: new Date().toISOString().slice(0, 10),
  valid_end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  sort_order: 0,
  enabled: true,
});

export function MiniprogramAdsPanel({ miniprogramId, sites, onUpload }: Props) {
  const [items, setItems] = useState<MiniproAdRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    if (miniprogramId < 1) return;
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        items?: MiniproAdRow[];
        total?: number;
        err?: string;
      }>('list_minipro_ads', {
        method: 'GET',
        query: { miniprogram_id: miniprogramId, page, per_page: PER_PAGE, q: appliedQ },
      });
      if (!r.ok) {
        setErr(r.err || '加载失败');
        return;
      }
      setItems(r.items || []);
      setTotal(r.total || 0);
    } finally {
      setLoading(false);
    }
  }, [miniprogramId, page, appliedQ]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setForm({ ...emptyForm(), miniprogram_id: miniprogramId });
    setModalOpen(true);
  }

  function openEdit(row: MiniproAdRow) {
    setForm({ ...row, site_ids: row.site_ids || [] });
    setModalOpen(true);
  }

  async function saveAd() {
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_minipro_ad', {
        method: 'POST',
        body: JSON.stringify({ ...form, miniprogram_id: miniprogramId }),
      });
      if (!r.ok) {
        setErr('保存失败：' + (r.err || 'unknown'));
        return;
      }
      setModalOpen(false);
      setMsg('广告已保存');
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function removeAd(id: number) {
    if (!window.confirm('确定删除该广告？')) return;
    const r = await apiFetch<{ ok: boolean }>('delete_minipro_ad', {
      method: 'POST',
      body: JSON.stringify({ id, miniprogram_id: miniprogramId }),
    });
    if (r.ok) {
      setMsg('已删除');
      await load();
    } else {
      setErr('删除失败');
    }
  }

  async function pickBanner(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const path = await onUpload(f);
    setForm((prev) => ({ ...prev, banner_image_path: path }));
  }

  async function pickTarget(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const path = await onUpload(f);
    setForm((prev) => ({ ...prev, target_image_path: path }));
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <>
      <Card title="首页横排广告管理">
        <p className="mb-4 text-sm text-slate-600">
          管理首页列表上方 5:1 横条广告。请在「参数配置 → 运营扩展」中开启「首页列表上方横排广告」后，小程序端才会展示。
          <Link to={`/miniprograms/${miniprogramId}?view=config`} className="ml-1 text-teal-700 underline">
            去参数配置
          </Link>
        </p>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className={`${labelClass} min-w-[200px] flex-1`}>
            <span>搜索</span>
            <input
              className={inputClass}
              value={q}
              placeholder="标题 / ID / 信息 ID"
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  setAppliedQ(q.trim());
                }
              }}
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setPage(1);
              setAppliedQ(q.trim());
            }}
          >
            查询
          </Button>
          <Button type="button" variant="primary" onClick={openCreate}>
            新建广告
          </Button>
        </div>
        {err ? <p className="mb-3 text-sm text-rose-700">{err}</p> : null}
        {msg ? <p className="mb-3 text-sm text-emerald-700">{msg}</p> : null}
        {loading ? (
          <p className="text-sm text-slate-500">加载中…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">暂无广告，点击「新建广告」添加。</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {items.map((row) => (
              <li key={row.id} className={`${listRowClass} flex-wrap gap-3 px-4 py-3`}>
                {row.banner_image_path ? (
                  <img
                    src={resolveMediaUrl(row.banner_image_path)}
                    alt=""
                    className="h-12 w-[200px] shrink-0 rounded border object-cover"
                    style={{ aspectRatio: '5 / 1' }}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{row.title || `广告 #${row.id}`}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {row.link_type === 'entry' ? `跳转信息 #${row.entry_id}` : '点击图片展示'}
                    {' · '}
                    {row.valid_start_date} ~ {row.valid_end_date}
                    {row.is_active ? (
                      <span className="ml-2 text-emerald-700">展示中</span>
                    ) : (
                      <span className="ml-2 text-amber-700">未在有效期</span>
                    )}
                    {!row.enabled ? <span className="ml-2 text-slate-400">已停用</span> : null}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(row)}>
                    编辑
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="!text-rose-700" onClick={() => void removeAd(row.id)}>
                    删除
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <PaginationBar
          className="mt-4"
          page={page}
          totalPages={totalPages}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </Card>

      <Modal open={modalOpen} title={form.id > 0 ? `编辑广告 #${form.id}` : '新建广告'} onClose={() => !saving && setModalOpen(false)}>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <label className={labelClass}>
            <span>标题（管理用）</span>
            <input className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className={labelClass}>
            <span>横条广告图（5:1）</span>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {form.banner_image_path ? (
                <img
                  src={resolveMediaUrl(form.banner_image_path)}
                  alt=""
                  className="h-14 max-w-full rounded border object-cover"
                  style={{ aspectRatio: '5 / 1' }}
                />
              ) : null}
              <label className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs">
                上传
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void pickBanner(e)} />
              </label>
            </div>
          </label>
          <label className={labelClass}>
            <span>点击行为</span>
            <select
              className={inputClass}
              value={form.link_type}
              onChange={(e) =>
                setForm({
                  ...form,
                  link_type: e.target.value === 'image' ? 'image' : 'entry',
                })
              }
            >
              <option value="entry">打开某条信息详情</option>
              <option value="image">打开图片（如二维码）</option>
            </select>
          </label>
          {form.link_type === 'entry' ? (
            <label className={labelClass}>
              <span>信息 ID</span>
              <input
                className={inputClass}
                type="number"
                min={1}
                value={form.entry_id || ''}
                onChange={(e) => setForm({ ...form, entry_id: Number(e.target.value) || 0 })}
              />
            </label>
          ) : (
            <label className={labelClass}>
              <span>点击展示图片</span>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {form.target_image_path ? (
                  <img
                    src={resolveMediaUrl(form.target_image_path)}
                    alt=""
                    className="h-24 w-24 rounded border object-cover"
                  />
                ) : null}
                <label className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs">
                  上传
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => void pickTarget(e)} />
                </label>
              </div>
            </label>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              <span>生效开始</span>
              <input
                type="date"
                className={inputClass}
                value={form.valid_start_date}
                onChange={(e) => setForm({ ...form, valid_start_date: e.target.value })}
              />
            </label>
            <label className={labelClass}>
              <span>生效结束</span>
              <input
                type="date"
                className={inputClass}
                value={form.valid_end_date}
                onChange={(e) => setForm({ ...form, valid_end_date: e.target.value })}
              />
            </label>
          </div>
          <label className={labelClass}>
            <span>排序（越小越靠前）</span>
            <input
              className={inputClass}
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            启用
          </label>
          <p className="text-xs text-slate-500">展示站点（不勾选表示全站）：</p>
          <SiteHierarchyCheckboxList
            sites={sites}
            selectedIds={form.site_ids}
            onToggle={(sid) => {
              const set = new Set(form.site_ids);
              if (set.has(sid)) set.delete(sid);
              else set.add(sid);
              setForm({ ...form, site_ids: [...set].sort((a, b) => a - b) });
            }}
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="secondary" disabled={saving} onClick={() => setModalOpen(false)}>
            取消
          </Button>
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveAd()}>
            保存
          </Button>
        </div>
      </Modal>
    </>
  );
}
